// components/common/table/FrequencyPicker.tsx
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { FREQUENCY_OPTIONS } from "@/utils/constants";
import CustomDropdown from "./CustomDropdown";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { calculateSmartPosition, PositionInfo } from "@/utils/dropdownPositioning";
import { PositionDebugOverlay } from "@/components/common/PositionDebugOverlay";

interface FrequencyPickerProps {
  value: string;
  onChange: (value: string) => void;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  onDateRangeChange: (type: 'startDate' | 'endDate', value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onBlur: (e: React.FocusEvent) => void;
  autoFocus?: boolean;
  debug?: boolean; // Enable positioning debug
}

const FrequencyPicker: React.FC<FrequencyPickerProps> = ({
  value,
  onChange,
  dateRange,
  onDateRangeChange,
  onKeyDown,
  onBlur,
  autoFocus = true,
  debug = false,
}) => {
  // State for frequency submenu
  const [frequencySubmenuPosition, setFrequencySubmenuPosition] = useState<PositionInfo | null>(null);
  const [hoveredFrequency, setHoveredFrequency] = useState<string>("");
  const [isSubmenuOpenedByKeyboard, setIsSubmenuOpenedByKeyboard] = useState<boolean>(false);
  const frequencySubmenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const frequencySubmenuRef = useRef<HTMLDivElement>(null);

  // State for calendar
  const [activeField, setActiveField] = useState<'start' | 'end'>('start');
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (dateRange.startDate) return new Date(dateRange.startDate);
    return new Date();
  });
  const [focusedDate, setFocusedDate] = useState<Date>(() => {
    if (activeField === 'start' && dateRange.startDate) return new Date(dateRange.startDate);
    if (activeField === 'end' && dateRange.endDate) return new Date(dateRange.endDate);
    return new Date();
  });

  const frequencyOptions = [
    { value: FREQUENCY_OPTIONS.NONE, label: "None" },
    { value: FREQUENCY_OPTIONS.MONTHLY, label: "Monthly" },
    { value: FREQUENCY_OPTIONS.QUARTERLY, label: "Quarterly" },
    { value: FREQUENCY_OPTIONS.YEARLY, label: "Yearly" },
  ];

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Constants for submenu dimensions
  const SUBMENU_WIDTH = 380;
  const SUBMENU_HEIGHT = 550; // Approximate height with calendar

  // Close frequency submenu when clicking outside
  useEffect(() => {
    if (!frequencySubmenuPosition) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        frequencySubmenuRef.current && 
        !frequencySubmenuRef.current.contains(target)
      ) {
        const mainDropdown = document.querySelector('[role="listbox"]');
        if (mainDropdown && !mainDropdown.contains(target)) {
          setFrequencySubmenuPosition(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [frequencySubmenuPosition]);

  // Handle keyboard navigation for calendar
  useEffect(() => {
    if (!frequencySubmenuPosition || isSubmenuOpenedByKeyboard) return; // Don't handle keyboard if opened by keyboard

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!frequencySubmenuRef.current?.contains(document.activeElement)) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 1);
            if (newDate.getMonth() !== currentMonth.getMonth()) {
              setCurrentMonth(new Date(newDate));
            }
            return newDate;
          });
          break;

        case 'ArrowRight':
          e.preventDefault();
          setFocusedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 1);
            if (newDate.getMonth() !== currentMonth.getMonth()) {
              setCurrentMonth(new Date(newDate));
            }
            return newDate;
          });
          break;

        case 'ArrowUp':
          e.preventDefault();
          setFocusedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            if (newDate.getMonth() !== currentMonth.getMonth()) {
              setCurrentMonth(new Date(newDate));
            }
            return newDate;
          });
          break;

        case 'ArrowDown':
          e.preventDefault();
          setFocusedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            if (newDate.getMonth() !== currentMonth.getMonth()) {
              setCurrentMonth(new Date(newDate));
            }
            return newDate;
          });
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          handleDateSelect(focusedDate);
          break;

        case 'Tab':
          e.preventDefault();
          if (activeField === 'start') {
            setActiveField('end');
            if (dateRange.endDate) {
              setFocusedDate(new Date(dateRange.endDate));
              setCurrentMonth(new Date(dateRange.endDate));
            }
          } else {
            setActiveField('start');
            if (dateRange.startDate) {
              setFocusedDate(new Date(dateRange.startDate));
              setCurrentMonth(new Date(dateRange.startDate));
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          setFrequencySubmenuPosition(null);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [frequencySubmenuPosition, isSubmenuOpenedByKeyboard, focusedDate, currentMonth, activeField, dateRange]);

  // Handle resize events
  useEffect(() => {
    if (!frequencySubmenuPosition) return;

    const handleResize = () => {
      // Recalculate position on resize
      const hoveredOption = document.querySelector(`[data-frequency-value="${hoveredFrequency}"]`) as HTMLElement;
      if (hoveredOption) {
        handleFrequencyOptionHover(hoveredFrequency, hoveredOption as HTMLButtonElement);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [frequencySubmenuPosition, hoveredFrequency]);

  const handleFrequencyChange = (newValue: string) => {
    onChange(newValue);
    setFrequencySubmenuPosition(null);
    
    if (newValue === FREQUENCY_OPTIONS.NONE) {
      onDateRangeChange('startDate', '');
      onDateRangeChange('endDate', '');
    } else if (!dateRange.startDate) {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      let endDate = new Date();
      
      switch(newValue) {
        case FREQUENCY_OPTIONS.MONTHLY:
          endDate.setMonth(endDate.getMonth() + 12);
          break;
        case FREQUENCY_OPTIONS.QUARTERLY:
          endDate.setMonth(endDate.getMonth() + 12);
          break;
        case FREQUENCY_OPTIONS.YEARLY:
          endDate.setFullYear(endDate.getFullYear() + 5);
          break;
      }
      
      onDateRangeChange('startDate', startDate);
      onDateRangeChange('endDate', endDate.toISOString().split('T')[0]);
    }
  };

  const handleFrequencyOptionHover = (
    optionValue: string,
    element: HTMLButtonElement | null,
    triggeredByKeyboard: boolean = false
  ) => {
    if (frequencySubmenuTimeoutRef.current) {
      clearTimeout(frequencySubmenuTimeoutRef.current);
    }

    setHoveredFrequency(optionValue);
    setIsSubmenuOpenedByKeyboard(triggeredByKeyboard);

    if (optionValue !== FREQUENCY_OPTIONS.NONE && element) {
      const parentDropdown = element.closest('[role="listbox"]') as HTMLElement;

      // Use the parent dropdown as the trigger element for better positioning
      const triggerElement = parentDropdown || element;

      const position = calculateSmartPosition(
        triggerElement,
        SUBMENU_WIDTH,
        SUBMENU_HEIGHT,
        {
          preferredPosition: 'right',
          parentElement: null,
          offset: 4, // Small offset from dropdown
          debug: debug
        }
      );

      setFrequencySubmenuPosition(position);
    } else {
      frequencySubmenuTimeoutRef.current = setTimeout(() => {
        setFrequencySubmenuPosition(null);
        setIsSubmenuOpenedByKeyboard(false);
      }, 100);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }

    // Add next month's leading days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const isInRange = (date: Date) => {
    if (!dateRange.startDate || !dateRange.endDate) return false;
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return date >= start && date <= end;
  };

  const getDayClassName = (day: { date: Date; isCurrentMonth: boolean }) => {
    const baseClasses = 'w-10 h-10 flex items-center justify-center text-sm font-medium rounded-lg cursor-pointer transition-all duration-200';
    
    if (!day.isCurrentMonth) {
      return `${baseClasses} text-gray-300 hover:bg-gray-50`;
    }

    const isToday = isSameDay(day.date, new Date());
    const isFocused = isSameDay(day.date, focusedDate);
    const isStartDate = dateRange.startDate && isSameDay(day.date, new Date(dateRange.startDate));
    const isEndDate = dateRange.endDate && isSameDay(day.date, new Date(dateRange.endDate));
    const isInRangeDate = isInRange(day.date);

    if (isStartDate || isEndDate) {
      return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700 ${isFocused ? 'ring-2 ring-blue-400' : ''}`;
    }

    if (isInRangeDate) {
      return `${baseClasses} bg-blue-100 text-blue-800 hover:bg-blue-200 ${isFocused ? 'ring-2 ring-blue-400' : ''}`;
    }

    if (isToday) {
      return `${baseClasses} ${
        isFocused 
          ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-400' 
          : 'text-blue-600 font-bold hover:bg-blue-50 border-2 border-dashed border-blue-300'
      }`;
    }

    return `${baseClasses} ${
      isFocused 
        ? 'bg-gray-100 ring-2 ring-blue-400' 
        : 'text-gray-700 hover:bg-gray-100'
    }`;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateSelect = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    onDateRangeChange(activeField === 'start' ? 'startDate' : 'endDate', dateStr);
    
    // Auto-switch to end date after selecting start date
    if (activeField === 'start') {
      setActiveField('end');
      setFocusedDate(date);
    }
  };

  const calculateOccurrences = (start: string, end: string, frequency: string): number => {
    if (!start || !end) return 0;
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    let count = 0;
    let current = new Date(startDate);
    
    while (current <= endDate) {
      count++;
      switch(frequency) {
        case FREQUENCY_OPTIONS.MONTHLY:
          current.setMonth(current.getMonth() + 1);
          break;
        case FREQUENCY_OPTIONS.QUARTERLY:
          current.setMonth(current.getMonth() + 3);
          break;
        case FREQUENCY_OPTIONS.YEARLY:
          current.setFullYear(current.getFullYear() + 1);
          break;
      }
    }
    
    return count;
  };

  const getQuickFillDates = (frequency: string): { start: string; end: string } => {
    const today = new Date();
    const endDate = new Date();
    
    switch(frequency) {
      case FREQUENCY_OPTIONS.MONTHLY:
        endDate.setMonth(endDate.getMonth() + 6);
        break;
      case FREQUENCY_OPTIONS.QUARTERLY:
        endDate.setMonth(endDate.getMonth() + 12);
        break;
      case FREQUENCY_OPTIONS.YEARLY:
        endDate.setFullYear(endDate.getFullYear() + 3);
        break;
    }
    
    return {
      start: today.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return 'Select date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const days = frequencySubmenuPosition ? getDaysInMonth(currentMonth) : [];

  // Position arrow indicator
  const getArrowStyles = () => {
    if (!frequencySubmenuPosition) return {};
    
    const position = frequencySubmenuPosition.position;
    const arrowSize = 8;
    
    if (position.startsWith('right')) {
      return {
        left: `-${arrowSize}px`,
        top: '20px',
        borderRight: `${arrowSize}px solid white`,
        borderTop: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid transparent`,
      };
    } else if (position.startsWith('left')) {
      return {
        right: `-${arrowSize}px`,
        top: '20px',
        borderLeft: `${arrowSize}px solid white`,
        borderTop: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid transparent`,
      };
    } else if (position.startsWith('bottom')) {
      return {
        top: `-${arrowSize}px`,
        left: '20px',
        borderBottom: `${arrowSize}px solid white`,
        borderLeft: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid transparent`,
      };
    } else {
      return {
        bottom: `-${arrowSize}px`,
        left: '20px',
        borderTop: `${arrowSize}px solid white`,
        borderLeft: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid transparent`,
      };
    }
  };

  // Add data attribute to frequency options for easy reference
  const enhancedFrequencyOptions = frequencyOptions.map(opt => ({
    ...opt,
    dataAttribute: { 'data-frequency-value': opt.value }
  }));

  return (
    <>
      <CustomDropdown
        dropdownWidth={220}
        minDropdownWidth={220}
        value={String(value || FREQUENCY_OPTIONS.NONE)}
        options={enhancedFrequencyOptions}
        onChange={handleFrequencyChange}
        placeholder="Select Frequency"
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        autoFocus={autoFocus}
        autoOpen={true}
        onOptionHover={handleFrequencyOptionHover}
        showArrowOnOption={hoveredFrequency !== FREQUENCY_OPTIONS.NONE ? hoveredFrequency : undefined}
      />

      {/* Debug Overlay */}
      {debug && frequencySubmenuPosition && <PositionDebugOverlay positionInfo={frequencySubmenuPosition} />}

      {/* Apple Calendar-style Date Range Submenu */}
      {frequencySubmenuPosition && hoveredFrequency !== FREQUENCY_OPTIONS.NONE &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={frequencySubmenuRef}
            className="fixed bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden transition-all duration-200"
            style={{
              top: `${frequencySubmenuPosition.top}px`,
              left: `${frequencySubmenuPosition.left}px`,
              width: `${SUBMENU_WIDTH}px`,
              zIndex: 10001,
              maxHeight: `${SUBMENU_HEIGHT}px`,
              transform: 'scale(1)',
              opacity: 1,
              pointerEvents: isSubmenuOpenedByKeyboard ? 'none' : 'auto',
            }}
            onMouseEnter={() => {
              if (frequencySubmenuTimeoutRef.current) {
                clearTimeout(frequencySubmenuTimeoutRef.current);
              }
              // When mouse enters, allow interaction
              setIsSubmenuOpenedByKeyboard(false);
            }}
            onMouseLeave={() => {
              frequencySubmenuTimeoutRef.current = setTimeout(() => {
                setFrequencySubmenuPosition(null);
                setIsSubmenuOpenedByKeyboard(false);
              }, 200);
            }}
          >
            {/* Arrow indicator */}
            <div 
              className="absolute w-0 h-0 z-10" 
              style={{
                ...getArrowStyles(),
                position: 'absolute',
              }}
            />

            <div className="p-4 overflow-auto max-h-full">
              {/* Header with frequency type */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-800">
                  {hoveredFrequency} Recurrence
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const { start, end } = getQuickFillDates(hoveredFrequency);
                    onDateRangeChange('startDate', start);
                    onDateRangeChange('endDate', end);
                    setCurrentMonth(new Date(start));
                    setFocusedDate(new Date(start));
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                  tabIndex={isSubmenuOpenedByKeyboard ? -1 : 0}
                >
                  Quick Fill
                </button>
              </div>

              {/* Date Range Display */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div
                  className={`p-2 border rounded-lg cursor-pointer transition-all ${
                    activeField === 'start'
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => {
                    if (!isSubmenuOpenedByKeyboard) {
                      setActiveField('start');
                      if (dateRange.startDate) {
                        const date = new Date(dateRange.startDate);
                        setCurrentMonth(date);
                        setFocusedDate(date);
                      }
                    }
                  }}
                  tabIndex={isSubmenuOpenedByKeyboard ? -1 : 0}
                >
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Start Date
                  </label>
                  <div className="text-sm font-medium text-gray-900">
                    {formatDateForDisplay(dateRange.startDate)}
                  </div>
                </div>

                <div
                  className={`p-2 border rounded-lg cursor-pointer transition-all ${
                    activeField === 'end'
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => {
                    if (!isSubmenuOpenedByKeyboard) {
                      setActiveField('end');
                      if (dateRange.endDate) {
                        const date = new Date(dateRange.endDate);
                        setCurrentMonth(date);
                        setFocusedDate(date);
                      }
                    }
                  }}
                  tabIndex={isSubmenuOpenedByKeyboard ? -1 : 0}
                >
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    End Date
                  </label>
                  <div className="text-sm font-medium text-gray-900">
                    {formatDateForDisplay(dateRange.endDate)}
                  </div>
                </div>
              </div>

              {/* Calendar */}
              <div className="border-t pt-3">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-md p-2">
                  <button
                    type="button"
                    onClick={() => navigateMonth('prev')}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    aria-label="Previous month"
                    tabIndex={isSubmenuOpenedByKeyboard ? -1 : 0}
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>

                  <h3 className="text-sm font-medium text-gray-700 select-none">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h3>

                  <button
                    type="button"
                    onClick={() => navigateMonth('next')}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    aria-label="Next month"
                    tabIndex={isSubmenuOpenedByKeyboard ? -1 : 0}
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-[2px] mb-2">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-[2px]">
                  {days.map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleDateSelect(day.date)}
                      className={getDayClassName(day)}
                      aria-label={`Select ${day.date.toLocaleDateString()}`}
                      tabIndex={isSubmenuOpenedByKeyboard ? -1 : 0}
                    >
                      {day.date.getDate()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Apply Button and Preview */}
              <div className="mt-4 space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    handleFrequencyChange(hoveredFrequency);
                  }}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  disabled={!dateRange.startDate || !dateRange.endDate}
                >
                  Apply {hoveredFrequency} Schedule
                </button>

                {/* Preview */}
                {dateRange.startDate && dateRange.endDate && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Preview:</span>{' '}
                      <span className="text-gray-900">
                        {calculateOccurrences(dateRange.startDate, dateRange.endDate, hoveredFrequency)} occurrences
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {hoveredFrequency.toLowerCase()} from{' '}
                      {new Date(dateRange.startDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}{' '}
                      to{' '}
                      {new Date(dateRange.endDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Debug info */}
              {debug && frequencySubmenuPosition.debug && (
                <div className="mt-4 p-2 bg-gray-100 rounded text-xs space-y-1">
                  <div className="font-semibold">Submenu Debug:</div>
                  <div>Position: {frequencySubmenuPosition.position}</div>
                  <div>Size: {SUBMENU_WIDTH}x{SUBMENU_HEIGHT}px</div>
                  <div>Available Space: 
                    T:{Math.round(frequencySubmenuPosition.debug.availableSpace.top)}px
                    R:{Math.round(frequencySubmenuPosition.debug.availableSpace.right)}px
                                        B:{Math.round(frequencySubmenuPosition.debug.availableSpace.bottom)}px
                    L:{Math.round(frequencySubmenuPosition.debug.availableSpace.left)}px
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default FrequencyPicker;